/* eslint-disable prettier/prettier */
/* eslint-disable no-console */
import type {
  UAAnalogControlFunction,
  UAAnalogSensorFunction,
  UAAnalogUnitRange,
  UADiscreteControlFunction,
  UADiscreteValue,
  UALADSDevice,
  UALADSFunctionalUnit
} from "../../server/lads-api/clients/lcc-openapi";
import {
  LADSDeviceStateMachineCurrentStateEnum
} from "../../server/lads-api/clients/lcc-openapi";
import { tripleFromBrowsePath } from "../util/browse-path";
import { formatRawValue } from "../util/format-value";
import { resolveNodeLabel } from "../util/node-label";
import type {
  ActiveProgram,
  Channel,
  Device,
  DeviceChannels,
  DeviceClass,
  Incident,
  SubClass,
  Unit,
} from "../view-model";

// Operational state (LADS DeviceStateMachine) → online/offline.
function mapLADSStateToDeviceStatus(
  state: LADSDeviceStateMachineCurrentStateEnum | undefined | null
): "online" | "offline" | "error" {
  if (!state || typeof state !== "string") {
    // Unknown state - assume offline (safer than assuming online)
    return "offline";
  }
  // Backend returns PascalCase (e.g., "Operate"), enum uses lowercase (e.g., "operate")
  const normalizedState = state.toLowerCase() as LADSDeviceStateMachineCurrentStateEnum;
  switch (normalizedState) {
    case LADSDeviceStateMachineCurrentStateEnum.Initialization:
    case LADSDeviceStateMachineCurrentStateEnum.Sleep:
    case LADSDeviceStateMachineCurrentStateEnum.Operate: {
      return "online";
    }
    case LADSDeviceStateMachineCurrentStateEnum.Shutdown: {
      return "offline";
    }
    default: {
      return "error";
    }
  }
}

// DeviceState is determined from BOTH signals:
//  1. StatusCode = connectivity. Anything not "Good" (Bad…/Uncertain… set by the
//     backend DeviceStatusService when a server stops syncing) — or a missing
//     statusCode — means we cannot reach the device → offline, regardless of state.
//  2. Only on a "Good" StatusCode does the operational state enum decide
//     (operate/sleep/init → online, shutdown → offline).
function resolveDeviceStatus(
  state: LADSDeviceStateMachineCurrentStateEnum | undefined | null,
  statusCode: string | undefined | null
): "online" | "offline" | "error" {
  if (!statusCode?.startsWith("Good")) return "offline";
  return mapLADSStateToDeviceStatus(state);
}

function mapLADSTypeToClassAndSubclass(type?: string): {
  deviceClass: DeviceClass;
  subclass: SubClass;
} {
  if (!type) return { deviceClass: "device", subclass: "unknown" };
  const [classStr, subclassStr] = type.split("/",2);
  return {
    deviceClass: classStr as DeviceClass,
    subclass: subclassStr as SubClass,
  };
}

function formatValue(dataItem: UAAnalogUnitRange | UADiscreteValue | undefined): string | number {
  if (!dataItem || dataItem.value === undefined || dataItem.value.value === undefined) {
    return "-";
  }
  const discreteItem = dataItem as UADiscreteValue;
  return formatRawValue(
    dataItem.value.value,
    dataItem.valuePrecision,
    discreteItem.enumStrings
  );
}

/**
 * Build channel ID for history API access
 * Format: deviceId/functionalUnitId/functionId
 *
 * Identity is the CANONICAL triple (from browsePath) when available: mounted
 * functions from different source devices may share a browseName, so the
 * mount-local triple is ambiguous — canonical ids keep caches (datapoints,
 * thresholds) and incident matching collision-free. The mount context lives
 * separately in `channel.mountDeviceId`.
 */
function buildChannelId(deviceId: string | undefined, functionalUnitId: string | undefined, functionId: string, variableType?: string, browsePath?: string): string {
  const canonical = browsePath ? tripleFromBrowsePath(browsePath) : null;
  const dev = canonical?.deviceId ?? deviceId;
  const fu = canonical?.functionalUnitId ?? functionalUnitId;
  const fn = canonical?.functionId ?? functionId;
  if (dev && fu) {
    const base = `${dev}/${fu}/${fn}`;
    return variableType ? `${base}/${variableType}` : base;
  }
  return variableType ? `${fn}/${variableType}` : fn;
}

export function createChannelFromFunction(
  func: UAAnalogSensorFunction,
  deviceId?: string,
  functionalUnitId?: string
): Channel {
  // type is the FunctionType hierarchy [base, ..., concrete] — incl. vendor-
  // custom concrete types (e.g. Lauda's PumpStageSelectorType) — so we key on
  // the base type.
  if (func.typeHierarchy?.includes("BaseControlFunctionType")) {
    return createChannelFromControlFunction(func as UAAnalogControlFunction, deviceId, functionalUnitId);
  }
  if (func.typeHierarchy?.includes("BaseSensorFunctionType")) {
    return createChannelFromSensorFunction(func, deviceId, functionalUnitId);
  }
  if (func.typeHierarchy?.includes("CoverFunctionType")) {
    return createChannelFromCoverFunction(func as UADiscreteControlFunction, deviceId, functionalUnitId);
  }

  console.warn(`Unknown channel type: ${func.type}, creating minimal channel for ${func.browseName}`);
  return {
    id: buildChannelId(deviceId, functionalUnitId, func.browseName || "", undefined, func.browsePath),
    browsePath: func.browsePath,
    mountDeviceId: deviceId,
    name: resolveNodeLabel(func, "Unknown"),
    status: "off",
    current: { value: "-", timestamp: new Date() },
  };
}

/**
 * Check if any variable in a function has historizing enabled.
 * Iterates over all properties looking for objects with a historizing attribute.
 */
function hasHistorizingVariable(func: Record<string, unknown>): boolean {
  return Object.values(func).some(
    (v) => v && typeof v === 'object' && 'historizing' in v && (v as { historizing?: boolean }).historizing === true
  );
}

/**
 * Get list of variable names that have historizing enabled.
 * Returns array like ["CurrentValue", "TargetValue"] or ["SensorValue"]
 */
function getHistorizedVariables(func: Record<string, unknown>): string[] {
  const knownVariables = ["sensorValue", "currentValue", "targetValue"];
  const result: string[] = [];

  for (const varName of knownVariables) {
    const variable = func[varName];
    if (variable && typeof variable === 'object' && 'historizing' in variable && (variable as { historizing?: boolean }).historizing === true) {
        // Capitalize first letter to match API path format
        result.push(varName.charAt(0).toUpperCase() + varName.slice(1));
      }
  }

  return result;
}

/**
 * Get engineeringUnits from any variable that has it.
 */
function getEngineeringUnits(func: Record<string, unknown>): string | undefined {
  for (const v of Object.values(func)) {
    if (v && typeof v === 'object' && 'engineeringUnits' in v) {
      const eu = (v as { engineeringUnits?: string }).engineeringUnits;
      if (eu) return eu;
    }
  }
  return undefined;
}

function createChannelFromControlFunction(
  func: UAAnalogControlFunction | UADiscreteControlFunction,
  deviceId?: string,
  functionalUnitId?: string
): Channel {

  // Get unit from any variable that has engineeringUnits
  const unit = getEngineeringUnits(func as Record<string, unknown>);

  // OPC UA historizing attribute - check any variable in the function
  const historizing = hasHistorizingVariable(func as Record<string, unknown>);

  // Get list of variables with historizing enabled (e.g., ["CurrentValue", "TargetValue"])
  const historizedVariables = getHistorizedVariables(func as Record<string, unknown>);

  const currentTimestamp = func.currentValue?.value?.serverTimestamp
    ?? func.currentValue?.value?.sourceTimestamp
    ?? new Date();

  const channel: Channel = {
    id: buildChannelId(deviceId, functionalUnitId, func.browseName || "", undefined, func.browsePath),
    browsePath: func.browsePath,
    mountDeviceId: deviceId,
    name: resolveNodeLabel(func),
    unit,
    valuePrecision: func.currentValue?.valuePrecision ?? func.targetValue?.valuePrecision,
    status: "on", // LADS isEnabled doesn't mean connectivity - values flow regardless
    historizing,
    historizedVariables: historizedVariables.length > 0 ? historizedVariables : undefined,
    controllable: func.typeHierarchy?.includes("BaseControlFunctionType"),
    controlFunctionState: func.controlFunctionState,
    alarmMonitor: func.alarmMonitor,
    port: func.port || undefined,
    enumStrings: (func.currentValue as UADiscreteValue)?.enumStrings,

    current: {
      value: formatValue(func.currentValue),
      timestamp: new Date(currentTimestamp),
      statusCode: func.currentValue?.value?.statusCode,
    },
  };

  if (func.targetValue?.value) {
    const targetTimestamp = func.targetValue.value.serverTimestamp
      ?? func.targetValue.value.sourceTimestamp
      ?? new Date();
    channel.targetValue = {
      channel: func.browseName || "",
      value: formatValue(func.targetValue),
      timestamp: new Date(targetTimestamp),
    };
  }
  return channel;
}

/** CoverFunctionType (z. B. Door, LCU-865): read-only Zustands-Channel aus currentValue. */
function createChannelFromCoverFunction(
  func: UADiscreteControlFunction,
  deviceId?: string,
  functionalUnitId?: string
): Channel {
  const timestamp = func.currentValue?.value?.serverTimestamp
    ?? func.currentValue?.value?.sourceTimestamp
    ?? new Date();

  return {
    id: buildChannelId(deviceId, functionalUnitId, func.browseName || "", undefined, func.browsePath),
    browsePath: func.browsePath,
    mountDeviceId: deviceId,
    name: resolveNodeLabel(func),
    valuePrecision: func.currentValue?.valuePrecision,
    status: "on",
    historizing: hasHistorizingVariable(func as Record<string, unknown>),
    controllable: false,
    enumStrings: (func.currentValue as UADiscreteValue)?.enumStrings,
    current: {
      value: formatValue(func.currentValue),
      timestamp: new Date(timestamp),
      statusCode: func.currentValue?.value?.statusCode,
    },
  };
}

function createChannelFromSensorFunction(
  func: UAAnalogSensorFunction,
  deviceId?: string,
  functionalUnitId?: string
): Channel {
  const timestamp = func.sensorValue?.value?.sourceTimestamp
    ?? func.sensorValue?.value?.serverTimestamp
    ?? new Date();

  // Get list of variables with historizing enabled
  const historizedVariables = getHistorizedVariables(func as Record<string, unknown>);

  return {
    id: buildChannelId(deviceId, functionalUnitId, func.browseName || "", undefined, func.browsePath),
    browsePath: func.browsePath,
    mountDeviceId: deviceId,
    name: resolveNodeLabel(func),
    unit: getEngineeringUnits(func as Record<string, unknown>),
    valuePrecision: func.sensorValue?.valuePrecision,
    status: "on", // LADS isEnabled doesn't mean connectivity
    historizing: hasHistorizingVariable(func as Record<string, unknown>),
    historizedVariables: historizedVariables.length > 0 ? historizedVariables : undefined,
    controllable: func.typeHierarchy?.includes("BaseControlFunctionType"),
    alarmMonitor: func.alarmMonitor,
    port: func.port || undefined,
    enumStrings: (func.sensorValue as UADiscreteValue)?.enumStrings,
    current: {
      value: formatValue(func.sensorValue),
      timestamp: new Date(timestamp),
      statusCode: func.sensorValue?.value?.statusCode,
    },
  };
}

export function mapFunctionSetToChannels(
  functions: (UAAnalogControlFunction | UADiscreteControlFunction | UAAnalogSensorFunction)[],
  deviceId?: string,
  functionalUnitId?: string
): DeviceChannels {
  const channels: DeviceChannels = {};

  for (const fu of functions) {
    const channel = createChannelFromFunction(fu, deviceId, functionalUnitId);
    channel.isEnabled = fu.isEnabled ?? true;
    // Record-Key muss eindeutig sein: zwei gemountete Functions verschiedener
    // Quellgeräte können denselben browseName tragen (LCU-875) — der
    // kanonische browsePath ist es nie.
    channels[fu.browsePath ?? fu.browseName!] = channel;
  }

  return channels;
}

function mapHierarchicalLocationToRoom(hierarchicalLocation?: string): string {
  return hierarchicalLocation?.trim() ?? "";
}

export function mapLADSDevicesToViewDevices(
  ladsDevices: UALADSDevice[]
): Device[] {
  return ladsDevices.map((device) => mapLADSDeviceToViewDevice(device));
}

export function mapLADSFunctionalUnitToViewDevice(
  ladsFunctionalUnit: UALADSFunctionalUnit,
  _functionSets: (UAAnalogControlFunction | UADiscreteControlFunction | UAAnalogSensorFunction)[] = [],
  parentId?: string
): Device {
  // This is not a real device it is treated as a Device for channel compatibility, only id and name are used
  // ID must be scoped to parent device to avoid store collisions (all SmartModules have a "SensorUnit")
  const fuName = ladsFunctionalUnit.browseName ?? "";
  const device: Device = {
    id: parentId ? `${parentId}/${fuName}` : fuName,
    name: ladsFunctionalUnit.displayName ?? "",
    type: ladsFunctionalUnit.type,
    serial: "",
    class: "device",
    subclass: "unknown",
    stateChannels: {},
    location: {
      room: "",
      position: "",
    },
    image: "",
    standard: "lads",
    parentId,
  };
  return device;
}

/**
 * Extract ActiveProgram from typed FunctionalUnit REST response.
 */
function extractActiveProgram(unit: UALADSFunctionalUnit): ActiveProgram | undefined {
  const ap = unit.programManager?.activeProgram;
  if (!ap) return undefined;

  return {
    currentProgramTemplate: ap.currentProgramTemplate ?? undefined,
    currentRuntime: ap.currentRuntime ?? undefined,
    currentStepName: ap.currentStepName ?? undefined,
    currentStepNumber: ap.currentStepNumber ?? undefined,
    currentStepRuntime: ap.currentStepRuntime ?? undefined,
    estimatedRuntime: ap.estimatedRuntime ?? undefined,
    estimatedStepNumbers: ap.estimatedStepNumbers ?? undefined,
    estimatedStepRuntime: ap.estimatedStepRuntime ?? undefined,
    currentPauseTime: ap.currentPauseTime ?? undefined,
    deviceProgramRunId: ap.deviceProgramRunId ?? undefined,
  };
}

export function mapLADSFunctionalUnitToViewUnit(
  ladsFunctionalUnit: UALADSFunctionalUnit,
  deviceId: string
): Unit {
  // Map all function sets to channels (not just enabled ones)
  // Pass deviceId and functionalUnitId for history API path
  const functionalUnitId = ladsFunctionalUnit.browseName ?? "";
  const functions = mapFunctionSetToChannels(ladsFunctionalUnit.functionSet || [], deviceId, functionalUnitId);

  // Map FunctionalUnitState from REST response
  const normalizedState = (ladsFunctionalUnit.functionalUnitState?.toLowerCase() || "off") as Unit["state"];

  // Extract ActiveProgram from REST response
  const activeProgram = extractActiveProgram(ladsFunctionalUnit);

  const unit: Unit = {
    // Identity = viewing device + canonical browsePath. Two same-named FUs
    // mounted onto one device (e.g. two "SensorUnit" smart modules on a
    // fumehood) carry distinct canonical paths → distinct ids → distinct tabs.
    // The deviceId prefix keeps a FU's source-view and mount-view separate.
    id: `${deviceId}/${ladsFunctionalUnit.browsePath ?? ladsFunctionalUnit.browseName ?? ""}`,
    browsePath: ladsFunctionalUnit.browsePath,
    name: resolveNodeLabel(ladsFunctionalUnit),
    type: ladsFunctionalUnit.type,
    state: normalizedState,
    functions,
    assetId: ladsFunctionalUnit.assetId ?? "",
    deviceId,
    activeProgram,
  };
  return unit;
}

export const mapLADSFunctionalUnitsToViewDevices = (
  ladsFunctionalUnits: UALADSFunctionalUnit[],
  // functionSetsMap: Map<string, UALADSFunctionSet[]> = new Map(),
  parentId?: string
): Device[] => ladsFunctionalUnits.map((funcUnit) =>
  //const functionSets = functionSetsMap.get(funcUnit.browseName ?? "") || [];
   mapLADSFunctionalUnitToViewDevice(funcUnit, undefined, parentId)
);

export const mapLADSFunctionalUnitsToViewUnits = (
  ladsFunctionalUnits: UALADSFunctionalUnit[],
  deviceId: string
): Unit[] => ladsFunctionalUnits.map((funcUnit) =>
  mapLADSFunctionalUnitToViewUnit(funcUnit, deviceId)
);

/**
 * Monitored devices (LCU-875): single functions of other devices are organized
 * directly below the device's own FunctionSet — there are no FunctionalUnits.
 * Present that device-level functionSet as ONE synthetic view-unit so the
 * detail page's existing unit/channel/WS pipeline renders the functions like a
 * normal FU. Channels keep their canonical source browsePath, so live WS
 * updates (emitted as `DeviceSet/<thisDevice>/FunctionSet/<fn>/…` aliases)
 * match via the usual `FunctionSet/<fn>` suffix.
 */
export function mapDeviceFunctionSetToViewUnit(
  ladsDevice: UALADSDevice,
  deviceId: string
): Unit {
  const functions = mapFunctionSetToChannels(
    ladsDevice.functionSet ?? [],
    deviceId,
    "FunctionSet"
  );
  return {
    id: `${deviceId}/DeviceSet/${deviceId}/FunctionSet`,
    browsePath: `DeviceSet/${deviceId}/FunctionSet`,
    name: "Monitoring",
    type: "FunctionSet",
    state: "on",
    functions,
    assetId: ladsDevice.assetId ?? "",
    deviceId,
  };
}

function mapDeviceHealthAlarmsToIncidents(alarms: UALADSDevice["deviceHealthAlarms"]): Incident[] {
  return alarms ? alarms.map(alarm => ({
    id: alarm.eventId.toString(),
    name: alarm.displayName,
    createdAt: alarm.time instanceof Date ? alarm.time.toISOString() : (alarm.time ?? ""),
    closedAt: null,
    status: alarm.activeState ? "open" : "closed",
    message: alarm.message,
    severity: alarm.severity >= 667 ? "critical" : "warning",
    code: 0,
    help: "",
    service: false,
    airflow: false,
    deviceId: alarm.sourceNode,
  })) : [];
}

export function mapLADSDeviceToViewDevice(ladsDevice: UALADSDevice): Device {
  // Skip devices without identification (e.g., DeviceFeatures node)
  const { deviceClass, subclass } = mapLADSTypeToClassAndSubclass(
    ladsDevice.identification?.deviceClass
  );
  const room = mapHierarchicalLocationToRoom(ladsDevice.hierarchicalLocation);

  const incidents: Incident[] = mapDeviceHealthAlarmsToIncidents(ladsDevice.deviceHealthAlarms || []);

  // Immer dieselbe Label-Kette: componentName → displayName → browseName.
  // Kein Model-Sonderfall.
  const getDisplayName = () => resolveNodeLabel(ladsDevice);

  let imageName: string = subclass;
  // special image lookup logic for fumehoods
  if (subclass === "fumehood") {
    imageName = ladsDevice.identification.productCode;
  }

  // Devices without a proper LADS deviceClass map to subclass "unknown"
  // (or undefined) — there's no meaningful type image for them. Point at the
  // generic "no image" placeholder instead of /devices/unknown.png, which is
  // a fume-hood picture and would be shown for freezers, pH-meters, etc.
  const image =
    imageName && imageName !== "unknown"
      ? `/devices/${imageName}.png`
      : "/devices/no-image.png";

  const device: Device = {
    id: ladsDevice.browseName,
    // Mounted functions live in the device-level functionSet (monitored devices)
    // with their CANONICAL source browsePath. Exposing them as device.channels
    // lets getIncidentsForDevice match mounted incidents (canonical context) to
    // the shell — so tile/list/"Fehler anzeigen" show errors without loading
    // units. Empty {} for regular devices (own incidents match via ownPrefix).
    channels: mapFunctionSetToChannels(
      ladsDevice.functionSet ?? [],
      ladsDevice.browseName,
      "FunctionSet"
    ),
    type: ladsDevice.identification?.model ?? "",
    serial: ladsDevice.identification?.serialNumber ?? "",
    class: deviceClass,
    subclass: subclass,
    name: getDisplayName(),
    model: ladsDevice.identification?.model,
    manufacturer: ladsDevice.identification?.manufacturer,
    image,
    deviceTypeImage: ladsDevice.deviceTypeImage,
    location: {
      room: room,
      position: "",
    },
    state: {
      // From BOTH: StatusCode (connectivity) gates offline, then the operational
      // state enum decides. Operational state also stays in stateChannels.
      status: resolveDeviceStatus(
        ladsDevice.deviceState?.currentState,
        ladsDevice.deviceState?.statusCode
      ),
    },
    stateChannels: {
      status: {
        id: "status",
        name: "Status",
        status: "on",
        current: {
          value: ladsDevice.deviceState?.currentState,
        },
      },
    },
    standard: "lads",
    incidents,
  };

  return device;
}
